import { useState } from 'react';
import { Heart, ThumbsUp, Meh, ThumbsDown, Trash2, Eye, EyeOff, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'react-hot-toast';

const RATING_OPTIONS = [
  {
    value: 'loved',
    label: 'Loved',
    icon: Heart,
        iconColor: 'text-stone-900',
    selectedBg: 'bg-stone-500',
    selectedText: 'text-stone-900'
  },
  {
    value: 'liked',
    label: 'Liked',
    icon: ThumbsUp,
    iconColor: 'text-stone-900',
    selectedBg: 'bg-stone-500',
    selectedText: 'text-stone-900'
  },
  {
    value: 'neutral',
    label: 'Meh',
    icon: Meh,
    iconColor: 'text-stone-900',
    selectedBg: 'bg-stone-500',
    selectedText: 'text-stone-900'
  },
  {
    value: 'hated',
    label: 'Disliked',
    icon: ThumbsDown,
    iconColor: 'text-stone-900',
    selectedBg: 'bg-stone-600',
    selectedText: 'text-stone-900'
  }
];

export default function QuickRatingInterface({
  movie,
  userId,
  currentStatus = 'unseen',
  currentRating = null,
  onUpdate
}) {
  const [loading, setLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const handleMarkAsSeen = async (rating = null) => {
    setLoading(true);

    // Optimistic update
    const optimisticHistory = {
      entertainment_id: movie.id,
      watch_rating: rating,
      watch_status: 'seen',
      watched_date: new Date().toISOString()
    };
    onUpdate?.(optimisticHistory);

    try {
      const response = await fetch('/api/watch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'mark-as-seen',
          userId,
          entertainmentId: movie.id,
          rating
        })
      });

      const data = await response.json();

      if (data.success) {
        const ratingOption = RATING_OPTIONS.find(r => r.value === rating);
        toast.success(
          rating
            ? `Marked as seen and rated "${ratingOption?.label}"`
            : 'Marked as seen!'
        );
        // Update with actual data from server
        onUpdate?.(data.history);
      } else {
        throw new Error(data.error || 'Failed to update');
      }
    } catch (error) {
      console.error('Error updating watch status:', error);
      toast.error(error.message || 'Failed to update');
      // Rollback optimistic update by marking as unseen
      onUpdate?.(null, movie.id);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRating = async (rating) => {
    setLoading(true);

    // Store previous rating for rollback
    const previousRating = currentRating;

    // Optimistic update
    const optimisticHistory = {
      entertainment_id: movie.id,
      watch_rating: rating,
      watch_status: 'seen',
      watched_date: new Date().toISOString()
    };
    onUpdate?.(optimisticHistory);

    try {
      const response = await fetch('/api/watch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'update-watch-rating',
          userId,
          entertainmentId: movie.id,
          rating
        })
      });

      const data = await response.json();

      if (data.success) {
        const ratingOption = RATING_OPTIONS.find(r => r.value === rating);
        toast.success(
          `Updated rating to "${ratingOption?.label}"`
        );
        // Update with actual data from server
        onUpdate?.(data.history);
      } else {
        throw new Error(data.error || 'Failed to update rating');
      }
    } catch (error) {
      console.error('Error updating rating:', error);
      toast.error(error.message || 'Failed to update rating');
      // Rollback to previous rating
      const rollbackHistory = {
        entertainment_id: movie.id,
        watch_rating: previousRating,
        watch_status: 'seen',
        watched_date: new Date().toISOString()
      };
      onUpdate?.(rollbackHistory);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsUnseen = async () => {
    setLoading(true);

    // Store previous state for rollback
    const previousRating = currentRating;

    // Optimistic update - remove from watched
    onUpdate?.(null, movie.id);

    try {
      const response = await fetch('/api/watch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'mark-as-unseen',
          userId,
          entertainmentId: movie.id
        })
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Moved back to watch list');
        // Confirm removal
        onUpdate?.(data.history, movie.id);
      } else {
        throw new Error(data.error || 'Failed to update');
      }
    } catch (error) {
      console.error('Error marking as unseen:', error);
      toast.error(error.message || 'Failed to update');
      // Rollback - restore previous watched state
      const rollbackHistory = {
        entertainment_id: movie.id,
        watch_rating: previousRating,
        watch_status: 'seen',
        watched_date: new Date().toISOString()
      };
      onUpdate?.(rollbackHistory);
    } finally {
      setLoading(false);
    }
  };

  // Compact toggle bar
  if (!isExpanded) {
    return (
      <button
        onClick={() => setIsExpanded(true)}
        className="w-full flex items-center justify-between px-3 py-2 bg-white hover:bg-slate-50 text-slate-900 text-xs font-medium rounded-xl transition-all border border-slate-900 group"
      >
        <div className="flex items-center gap-2">
          {currentStatus === 'seen' ? (
            <>
              <Eye className="w-4 h-4" />
              <span>
                {currentRating 
                  ? `Watched • ${RATING_OPTIONS.find(r => r.value === currentRating)?.label}`
                  : 'Watched'
                }
              </span>
            </>
          ) : (
            <>
              <EyeOff className="w-4 h-4" />
              <span>Rate & Mark Watched</span>
            </>
          )}
        </div>
        <ChevronDown className="w-4 h-4 group-hover:text-stone-900 transition-colors" />
      </button>
    );
  }

  // Expanded view - If unseen, show "Mark as Seen" + rating buttons
  if (currentStatus === 'unseen' || currentStatus === 'want_to_watch') {
    return (
      <div className="space-y-3">
        <button
          onClick={() => setIsExpanded(false)}
          className="w-full flex items-center justify-between text-xs font-semibold text-stone-600 uppercase tracking-wide hover:text-stone-900 transition-colors"
        >
          Rate & Mark Watched
          <ChevronUp className="w-4 h-4" />
        </button>

        {/* Minimalist rating buttons - circular icon-only */}
        <div className="flex gap-2 justify-between">
          {RATING_OPTIONS.map(({ value, label, icon: Icon, iconColor, selectedBg }) => (
            <button
              key={value}
              onClick={() => handleMarkAsSeen(value)}
              disabled={loading}
              className="group flex flex-col items-center gap-1.5 transition-all disabled:opacity-50"
              title={label}
            >
              <div className={`w-10 h-10 rounded-full border-2 border-stone-900 flex items-center justify-center transition-all group-hover:border-stone-300 group-hover:bg-stone-50`}>
                <Icon className={`w-5 h-5 ${iconColor} transition-transform group-hover:scale-110`} />
              </div>
              <span className="text-xs font-medium text-stone-600">{label}</span>
            </button>
          ))}
        </div>

        {/* Seen without rating */}
        <button
          onClick={() => handleMarkAsSeen(null)}
          disabled={loading}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-white hover:bg-stone-50 text-stone-600 text-xs font-medium rounded-lg transition-all disabled:opacity-50 border border-stone-200"
        >
          <Eye className="w-4 h-4" />
          Mark Watched (No Rating)
        </button>
      </div>
    );
  }

  // Expanded view - If seen, show current rating + option to change
  return (
    <div className="space-y-3">
      <button
        onClick={() => setIsExpanded(false)}
        className="w-full flex items-center justify-between hover:text-stone-900 transition-colors"
      >
        <div className="text-xs font-semibold text-stone-600 uppercase tracking-wide">
          Your Rating
        </div>
        <div className="flex items-center gap-2">
          {currentRating && (
            <span className="text-xs font-semibold text-stone-900">
              {RATING_OPTIONS.find(r => r.value === currentRating)?.label}
            </span>
          )}
          <ChevronUp className="w-4 h-4 text-stone-600" />
        </div>
      </button>

      {/* Minimalist rating buttons - circular with selected state */}
      <div className="flex gap-2 justify-between">
        {RATING_OPTIONS.map(({ value, label, icon: Icon, iconColor, selectedBg, selectedText }) => {
          const isSelected = currentRating === value;
          return (
            <button
              key={value}
              onClick={() => handleUpdateRating(value)}
              disabled={loading}
              className="group flex flex-col items-center gap-1.5 transition-all disabled:opacity-50"
              title={label}
            >
              <div className={`w-10 h-10 rounded-full border-2 flex items-center justify-center transition-all ${
                isSelected
                  ? `${selectedBg} border-transparent`
                  : 'border-stone-200 group-hover:border-stone-300 group-hover:bg-stone-50'
              }`}>
                <Icon className={`w-5 h-5 transition-transform group-hover:scale-110 ${
                  isSelected ? 'text-white' : iconColor
                }`} />
              </div>
              <span className={`text-xs font-medium ${isSelected ? 'text-stone-900' : 'text-stone-600'}`}>
                {label}
              </span>
            </button>
          );
        })}
      </div>

      {/* Mark as unseen option */}
      <button
        onClick={handleMarkAsUnseen}
        disabled={loading}
        className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-white hover:bg-stone-50 text-stone-600 text-xs font-medium rounded-lg transition-all disabled:opacity-50 border border-stone-200"
      >
        <EyeOff className="w-4 h-4" />
        Mark as Unwatched
      </button>
    </div>
  );
}
